/* RESPONSIBLE TEAM: team-reporting */
import type Store from '@ember-data/store';
import { inject as service } from '@ember/service';
import Service from '@ember/service';
import {
  DayOfWeek,
  DayPosition,
  EVERY_DAY,
  type ExportRule,
  SchedulePreset,
  Step,
} from 'embercom/models/reporting/dataset-export-schedule';
import type IntlService from 'embercom/services/intl';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { isEqual } from 'underscore';

export default class DatasetExportScheduleService extends Service {
  @service declare store: Store;
  @service declare intl: IntlService;

  private defaultDeliveryTime(timezone: string) {
    return { hour: 12, minute: 0, timezone };
  }

  private weeklyExportRule(daysOfWeek: number[], timezone: string): ExportRule {
    return {
      daysOfWeek,
      deliveryTime: this.defaultDeliveryTime(timezone),
      interval: { step: 'weekly', value: 1, dayPosition: DayPosition.FIRST },
    };
  }

  isPresetRule(exportRule: ExportRule, preset: SchedulePreset, timezone: string): boolean {
    let rule = this.exportRuleFrom(preset, timezone);
    return (
      isEqual(rule.daysOfWeek, exportRule.daysOfWeek) && isEqual(rule.interval, exportRule.interval)
    );
  }

  exportRuleFrom(preset: SchedulePreset, timezone: string): ExportRule {
    switch (preset) {
      case SchedulePreset.DAILY:
        return this.weeklyExportRule(EVERY_DAY, timezone);
      case SchedulePreset.EVERY_MONDAY:
        return this.weeklyExportRule([DayOfWeek.MONDAY], timezone);
      case SchedulePreset.EVERY_FRIDAY:
        return this.weeklyExportRule([DayOfWeek.FRIDAY], timezone);
      case SchedulePreset.FIRST_DAY_OF_MONTH:
        return {
          daysOfWeek: EVERY_DAY,
          deliveryTime: this.defaultDeliveryTime(timezone),
          interval: { step: 'monthly', value: 1, dayPosition: DayPosition.FIRST },
        };
      case SchedulePreset.LAST_DAY_OF_MONTH:
        return {
          daysOfWeek: EVERY_DAY,
          deliveryTime: this.defaultDeliveryTime(timezone),
          interval: { step: 'monthly', value: 1, dayPosition: DayPosition.LAST },
        };
      default:
        throw new Error(`Unknown preset: ${preset}`);
    }
  }

  private get weekDays() {
    return [
      this.intl.t('app.lib.reporting.week-days.sunday'),
      this.intl.t('app.lib.reporting.week-days.monday'),
      this.intl.t('app.lib.reporting.week-days.tuesday'),
      this.intl.t('app.lib.reporting.week-days.wednesday'),
      this.intl.t('app.lib.reporting.week-days.thursday'),
      this.intl.t('app.lib.reporting.week-days.friday'),
      this.intl.t('app.lib.reporting.week-days.saturday'),
    ];
  }

  customLabel(schedule: DatasetExportSchedule) {
    let step = schedule.exportRule.interval.step;
    let daysOfWeek = schedule.exportRule.daysOfWeek;
    let interval = schedule.exportRule.interval.value;
    let dayPosition = schedule.exportRule.interval.dayPosition;
    let daysOfWeekLabels = daysOfWeek.map((dayIndex) => {
      return this.weekDays[dayIndex];
    });

    if (step === Step.WEEKLY && daysOfWeek.length === 7) {
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.daily',
      );
    } else if (step === Step.WEEKLY && interval === 1) {
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.weekly',
        {
          dayOfWeek: daysOfWeek.map((dayIndex) => this.weekDays[dayIndex]).join(', '),
        },
      );
    } else if (step === Step.MONTHLY && interval === 1 && daysOfWeek.length === 1) {
      let dayPositionLabel = this.intl.t(
        `reporting.data-export.schedule-export.new-schedule.day-position.${dayPosition}`,
      );
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.monthly',
        {
          dayPosition: dayPositionLabel.toLowerCase(),
          dayOfWeek: this.weekDays[daysOfWeek[0]],
        },
      );
    } else if (step === Step.MONTHLY && interval === 1 && daysOfWeek.length === 7) {
      return this.intl.t(
        `reporting.data-export.schedule-export.new-schedule.frequency-dropdown.monthly-on-${dayPosition}-day`,
      );
    } else if (step === Step.MONTHLY && interval > 1 && daysOfWeek.length === 7) {
      return this.intl.t(
        `reporting.data-export.schedule-export.new-schedule.frequency-dropdown.nth-monthly-on-${dayPosition}-day`,
        {
          interval,
        },
      );
    } else {
      return this.intl.t(
        'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.custom-frequency',
        {
          interval: interval === 1 ? '' : interval,
          step:
            interval === 1
              ? this.intl.t(
                  `reporting.data-export.schedule-export.new-schedule.frequency-dropdown.custom-frequency-dropdown.${step}`,
                )
              : this.intl.t(
                  `reporting.data-export.schedule-export.new-schedule.frequency-dropdown.custom-frequency-dropdown.multiple-${step}`,
                ),
          daysOfWeek: daysOfWeekLabels.join(', '),
        },
      );
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    datasetExportScheduleService: DatasetExportScheduleService;
    'dataset-export-schedule-service': DatasetExportScheduleService;
  }
}
